import { Injectable, inject } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { SentryService } from './sentry.service';

/**
 * `sessionStorage` key backing the reload cooldown — see {@link RELOAD_COOLDOWN_MS}.
 */
const RELOAD_GUARD_KEY = 'app_reload_guard_at';

/**
 * Minimum time (ms) between auto-reloads triggered by this service.
 *
 * @remarks
 * Without this, a genuine persistent failure (e.g. the CDN itself down, not
 * just one stale chunk) would reload-loop forever: reload → same navigation
 * fails again → reload again. Once the cooldown is hit, the failure is
 * reported to Sentry instead of retried, so it stays *visible* rather than
 * silently spinning the officer's device.
 */
const RELOAD_COOLDOWN_MS = 15_000;

/**
 * Pattern matching a lazy-route chunk that failed to load.
 *
 * @remarks
 * `"Failed to fetch dynamically imported module"` is the exact text Angular's
 * esbuild-based build (`@angular/build`, used by this app's `ng build`) throws
 * when a route's dynamic `import()` 404s — confirmed from a live Sentry event.
 * `ChunkLoadError`/`Loading chunk .* failed` are the equivalent messages from
 * the older webpack-based Angular builder and other bundlers; matched too in
 * case the build tooling ever changes, or a dependency's own dynamic import
 * fails with that wording.
 */
const CHUNK_LOAD_FAILURE_PATTERN =
  /Failed to fetch dynamically imported module|ChunkLoadError|Loading chunk .* failed/i;

/**
 * Recovers the app from running a stale, already-loaded bundle after a new
 * deployment — the standard failure mode for a long-lived SPA tab spanning a
 * CDN release.
 *
 * @remarks
 * **Why this exists (2026-08-05).** Reported live: a process server had the
 * app open, tapped "Sign now" to open an affidavit, and Angular's router
 * threw `TypeError: Failed to fetch dynamically imported module:
 * https://develop.d106lfu42lft1a.amplifyapp.com/chunk-BUIOHYN4.js`. Root
 * cause: every branch auto-deploys to Amplify on push (see the deployment
 * strategy), so a tab left open across a deploy still references the
 * *previous* build's content-hashed chunk filenames — a newer deploy replaces
 * those files on the CDN, so the old URL 404s and the dynamic `import()`
 * Angular Router uses for lazy routes (`app.routes.ts`) rejects. Previously
 * nothing recovered from this: `ErrorHandler` is replaced with
 * `Sentry.createErrorHandler()` (`app.config.ts`), which only *reports*
 * errors, never takes recovery action, so the officer was simply stuck on a
 * broken navigation.
 *
 * **Two complementary detection paths, both funnelling into {@link reload}:**
 * 1. **Reactive (the safety net):** subscribes to `Router.events` for
 *    `NavigationError`, and reloads when the error message matches
 *    {@link CHUNK_LOAD_FAILURE_PATTERN}. This is what catches the failure
 *    described above, after the fact.
 * 2. **Proactive (catches it earlier when it fires in time):** when the
 *    Angular service worker is enabled (`provideServiceWorker()` in
 *    `app.config.ts` — added the same day as this service, since it had been
 *    built by `angular.json` but never actually registered before this fix),
 *    subscribes to `SwUpdate.versionUpdates` for `VERSION_READY` and reloads
 *    as soon as the SW detects a new deployment — often before the officer
 *    ever navigates to a route whose chunk has gone stale.
 *
 * **The reload is silent — no confirmation prompt, per explicit product
 * direction.** The officer loses whatever screen they were mid-navigation to;
 * `window.location.reload()` re-fetches the current `index.html` and its
 * (now-current) chunk manifest, landing them back on a working app in roughly
 * one to two seconds. A confirm-then-reload or toast-first variant was
 * considered and rejected as unnecessary extra latency for what's already a
 * broken screen.
 *
 * **The cooldown guard is the one thing standing between this and an
 * infinite reload loop.** If the underlying cause isn't actually a stale
 * chunk (e.g. the CDN is genuinely unreachable), reloading would just
 * reproduce the same `NavigationError` immediately. {@link RELOAD_COOLDOWN_MS}
 * caps this to one reload per window, using `sessionStorage` so the guard
 * itself survives the reload (an in-memory flag would not). Once the cooldown
 * is active, the failure is reported to Sentry instead of retried — visible
 * and diagnosable, rather than silently spinning.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly router  = inject(Router);
  private readonly swUpdate = inject(SwUpdate);
  private readonly sentry  = inject(SentryService);

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe((event) => this.onNavigationError(event));

    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
        .subscribe(() => this.reload('sw-version-ready'));
    }
  }

  /**
   * Reloads when a `NavigationError`'s message matches
   * {@link CHUNK_LOAD_FAILURE_PATTERN}; every other navigation error is left
   * alone — those are genuine app bugs Sentry's `ErrorHandler` should keep
   * surfacing normally, not something a reload would fix.
   *
   * @param event - The router's `NavigationError` event.
   */
  private onNavigationError(event: NavigationError): void {
    const message = event.error instanceof Error ? event.error.message : String(event.error);
    if (CHUNK_LOAD_FAILURE_PATTERN.test(message)) {
      this.reload('chunk-load-failure');
    }
  }

  /**
   * Reloads the page, guarded by {@link RELOAD_COOLDOWN_MS} so a persistent
   * (non-stale-chunk) failure can't reload-loop.
   *
   * @param reason - Which detection path triggered this — attached to the
   *                 Sentry report either way, so the two paths (and a
   *                 suppressed repeat) are distinguishable in production.
   */
  private reload(reason: 'chunk-load-failure' | 'sw-version-ready'): void {
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    const now = Date.now();

    if (now - lastReloadAt < RELOAD_COOLDOWN_MS) {
      this.sentry.captureMessage(
        `AppUpdateService: reload suppressed (cooldown) — reason: ${reason}`,
        'warning',
      );
      return;
    }

    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
    this.sentry.captureMessage(`AppUpdateService: reloading — reason: ${reason}`, 'info');
    this.performReload();
  }

  /**
   * Thin wrapper around `window.location.reload()`.
   *
   * @remarks
   * Extracted purely so tests can mock the navigation itself
   * (`jest.spyOn(AppUpdateService.prototype, 'performReload')`) instead of
   * `window.location` — jsdom's `location` and `location.reload` are both
   * non-configurable/read-only in this repo's Jest setup, so neither
   * `Object.defineProperty(window, 'location', ...)` nor a direct
   * `window.location.reload = jest.fn()` assignment can succeed.
   */
  private performReload(): void {
    window.location.reload();
  }
}
